import type { LuckyWheelItem } from '@/lib/buildLuckyWheelItems'
import {
  buildCampaignBrandPalette,
  hexToRgb,
  rgbToHsl,
  hslHex,
} from '@/lib/campaignBrandPalette'
import { isLegacyRouletteNoneLabel, ROULETTE_NONE_EMOJI } from '@/lib/rouletteNoneDisplay'

export const WHEEL_CENTER = 250
export const WHEEL_RADIUS = 210
export const SPIN_SPEED = 960
export const SPIN_RAMP_MS = 850

/** Keep the campaign hue, with readable surfaces even for white/yellow/black brands. */
export function buildRoulettePalette(color: string) {
  const palette = buildCampaignBrandPalette(color)
  const rgb = hexToRgb(color)
  const { h, s } = rgbToHsl(rgb.r, rgb.g, rgb.b)
  return {
    ...palette,
    dark: hslHex(h, Math.min(80, s), 23),
    deep: hslHex(h, Math.min(65, s), 12),
    sliceB: hslHex(h, Math.min(70, s), 28),
  }
}

export function rouletteButtonSurface(primary: string, textColor: string) {
  const rgb = hexToRgb(primary)
  const { h, s } = rgbToHsl(rgb.r, rgb.g, rgb.b)
  const text = hexToRgb(textColor)
  const luminance = (text.r * 0.2126 + text.g * 0.7152 + text.b * 0.0722) / 255
  return hslHex(h, Math.min(70, s), luminance > 0.5 ? 18 : 92)
}

export function polarPoint(radius: number, angle: number) {
  const radians = (angle * Math.PI) / 180
  return { x: 250 + radius * Math.sin(radians), y: 250 - radius * Math.cos(radians) }
}

export function slicePath(index: number, count: number) {
  const angle = 360 / count
  const start = polarPoint(WHEEL_RADIUS, index * angle)
  const end = polarPoint(WHEEL_RADIUS, (index + 1) * angle)
  return `M 250 250 L ${start.x} ${start.y} A 210 210 0 ${angle > 180 ? 1 : 0} 1 ${end.x} ${end.y} Z`
}

/** Integral of a smooth acceleration: the wheel starts with zero velocity. */
export function launchAngle(elapsedMs: number) {
  const seconds = elapsedMs / 1000
  const ramp = SPIN_RAMP_MS / 1000
  if (seconds >= ramp) return SPIN_SPEED * (seconds - ramp / 2)
  const t = seconds / ramp
  return SPIN_SPEED * ramp * (t ** 3 - t ** 4 / 2)
}

/** Always moves forward; the winning sector's center ends exactly under the pin. */
export function landingTrajectory(current: number, position: number, count: number) {
  const target = (((360 - ((position - 0.5) * 360) / count) % 360) + 360) % 360
  const minimum = current + SPIN_SPEED * 0.95
  const end = target + Math.ceil((minimum - target) / 360) * 360
  return { end, duration: ((end - current) * 4 * 1000) / SPIN_SPEED }
}

export function brakingAngle(start: number, end: number, progress: number) {
  const t = Math.max(0, Math.min(1, progress))
  return start + (end - start) * (1 - (1 - t) ** 4)
}

export function payloadNumber(value: unknown): number | null {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value)
        : NaN
  return Number.isFinite(parsed) ? parsed : null
}

export const rouletteCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function prizeLabel(item: LuckyWheelItem): { value: string; type: string } {
  const prize = item.premio
  if (!prize) return { value: 'Sem prêmio', type: '' }
  const display = prize.display
  if (prize.tipo === 'coupon') {
    const percent = payloadNumber(display.discount_percent)
    const amount = payloadNumber(display.discount_amount)
    return {
      value: percent
        ? `${percent}%`
        : amount
          ? `R$ ${rouletteCurrency(amount)}`
          : display.description?.trim() || display.label?.trim() || 'Cupom',
      type: 'CUPOM',
    }
  }
  if (prize.tipo === 'lucky_number') {
    const quantity = payloadNumber(display.quantity)
    return { value: quantity !== null ? `+${quantity}` : 'Números', type: 'NÚMEROS' }
  }
  if (prize.tipo === 'pix')
    return {
      value:
        payloadNumber(display.amount) !== null
          ? `R$ ${rouletteCurrency(payloadNumber(display.amount)!)}`
          : 'PIX',
      type: 'PIX',
    }
  const label = display.label?.trim()
  return {
    value:
      label && !isLegacyRouletteNoneLabel(label) && label !== ROULETTE_NONE_EMOJI
        ? label
        : 'Sem prêmio',
    type: '',
  }
}

export function compactSliceLabel(value: string, count: number) {
  const max = count > 8 ? 12 : 17
  const chars = Array.from(value)
  return chars.length > max ? `${chars.slice(0, max - 1).join('')}…` : value
}
